/**
 * Household helpers. The store mirrors the active household id; the server's
 * `/api/auth/session` remains authoritative. Switching households does a
 * `queryClient.clear()` + a full page reload per ARCH §8.4 to prevent any
 * cross-household state bleed in cached components.
 */
import { useQueryClient } from '@tanstack/react-query';
import type { HouseholdId } from '@shared/types';
import { useAppStore, useToast } from './store';
import { useSetActiveHousehold } from './queries';

export function useActiveHouseholdId(): HouseholdId | null {
  return useAppStore((s) => s.activeHouseholdId);
}

/**
 * Reconcile the store's mirror with the server's authoritative active
 * household id. Call this from the root component after `useSession` resolves.
 */
export function reconcileActiveHousehold(
  serverActive: HouseholdId | null,
): void {
  const current = useAppStore.getState().activeHouseholdId;
  if (current !== serverActive) {
    useAppStore.getState().setActiveHousehold(serverActive);
  }
}

/**
 * Switch active household: POST the server, clear caches, full reload.
 */
export function useSwitchHousehold() {
  const setActive = useSetActiveHousehold();
  const qc = useQueryClient();
  const toast = useToast();
  return {
    isPending: setActive.isPending,
    switchTo: async (household_id: HouseholdId) => {
      try {
        await setActive.mutateAsync({ household_id });
        useAppStore.getState().setActiveHousehold(household_id);
        qc.clear();
        // Full reload — guarantees no React-state bleed from the prior household.
        if (typeof window !== 'undefined') {
          window.location.assign('/');
        }
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "We couldn't switch households.",
        );
      }
    },
  };
}
